/**
 * 
  channel购买：/api/rest/dlt/commerce/prepayment
  channel轮训：/api/rest/dlt/commerce/order/query
  strip购买：/api/rest/dlt/commerce/subscribe
  strip轮训：/api/rest/dlt/commerce/thirdStatus

  套餐购买
  B站国内
    channel购买、channel轮训
  B站海外
    stripe购买、strip轮训
  F站
    stripe购买，stripe轮训

  单次购买：
  B站国内
    prepayment-alipay、channel轮训 = B站国内套餐
  B站海外
    prepayment-stripe、stripe轮训
  F站
    prepayment-stripe，stripe轮训
 */

import React, { useEffect } from 'react';

import Loading from '@/components/common/Loading';
import { Commodity } from '@/components/RightProvider/const';
import { UseTranslateType } from '@/hooks/useTranslate';
import { IDENTITYKEY } from '@/store/User';
import { AIFunction } from '@/utils/const';
import {
  EventNewID,
  SubscribeReasonEnum,
  SubscriptionTypeEnum,
} from '@/utils/eventlog/eventNewConfig';
import { trackEvent } from '@/utils/eventlog/logAdapter';

import CommonModal from '..';
import Footer from './components/footer';
import SingleContent from './components/SinglePanel';
import usePayment from './hook/usePayment';
import CloseIcon from './icon/close';

export type TabActiveKey = 'once';
// 支付时保留一下对应的信息
export type OrderInfo = {
  orderId: string;
  orderType: 'once' | '';
  orderCommodityCode: string;
};
interface PaymentModalProps {
  t?: UseTranslateType;
  showBanner?: boolean;
  customTip?: string;
  autoNext?: boolean;
  feature?: AIFunction;
  activeTab?: TabActiveKey;
  onOk?: () => void;
  onClose?: () => void;
  reason?: SubscribeReasonEnum;
}

export interface TabContentProps {
  userKey: IDENTITYKEY;
  activeKey: TabActiveKey;
  disCount: number;
  disabled: boolean;
  onChange: (value: TabActiveKey) => void;
}

const Title = ({
  customTip,
  onClose,
  t,
}: Pick<PaymentModalProps, 'customTip' | 'onClose' | 't'>) => (
  <div className="flex items-center justify-center  p-[28px] relative">
    <div className={`text-[32px]  pb-[8px] text-center font-[600] font-sans`}>
      {t && t('Ai Credits')}
    </div>
    <div
      className="absolute top-[17px] right-[24px] flex flex-row items-center justify-center cursor-pointer"
      onClick={onClose}
    >
      <CloseIcon />
    </div>
  </div>
);

const Banner = ({ t }: { t: UseTranslateType }) => {
  return (
    <div
      className=" bg-[#2D2F82] text-color-text-0  absolute top-[-103px] left-1/2 -translate-x-1/2 text-[18px]"
      style={{
        width: 'auto',
        padding: '16px 68px',
        borderRadius: '8px',
        border: '1px solid rgba(86, 97, 241, 0.70)',
        boxShadow: `0px 4px 14px 0px rgba(0, 0, 0, 0.10), 0px 0px 1px 0px rgba(0, 0, 0, 0.30)`,
      }}
    >
      {t('Your credits are insufficient Please purchase credits')}
    </div>
  );
};

const PaymentContent: React.FC<PaymentModalProps> = ({
  showBanner = false,
  customTip,
  feature = AIFunction.Other,
  autoNext = false,
  onClose,
  onOk,
  t,
}: PaymentModalProps) => {
  const {
    handlePay,
    purchaseLimitLoading,
    highlightSingleCode,
    formatSingleData,
    isPurchaseSuccess,
    singleLoadingStates,
  } = usePayment({
    t,
  });

  useEffect(() => {
    if (isPurchaseSuccess) {
      onClose && onClose();
      if (autoNext) {
        onOk && onOk();
      }
    }
  }, [isPurchaseSuccess]);

  const onSingleClick = async (
    data: Commodity[number],
    btnIndex: number,
    price: string,
    currency: string,
  ) => {
    await handlePay({
      data,
      btnIndex,
      feature,
      price,
      currency,
    });
  };
  if (!t) {
    return;
  }

  return (
    <div className="bg-payment-modal-bg !rounded-[8px] overflow-hidden relative">
      <div
        className="absolute"
        style={{
          left: '10%',
          top: '-30%',
          width: '605px',
          height: '605px',
          borderRadius: '405px',
          background: '#BFADFF',
          filter: 'blur(110px)',
        }}
      ></div>

      <div
        className="absolute"
        style={{
          right: '10%',
          top: '-30%',
          width: '605px',
          height: '605px',
          borderRadius: '405px',
          background: '#BFC7FB',
          filter: 'blur(110px)',
        }}
      ></div>
      {showBanner ? <Banner t={t} /> : null}
      <div className=" overflow-hidden">
        <Title customTip={customTip} onClose={onClose} t={t} />
        <div className="relative min-h-[280px]">
          {purchaseLimitLoading ? <Loading size={50} /> : null}
          <SingleContent
            highlightCode={highlightSingleCode}
            data={formatSingleData}
            loadingStates={singleLoadingStates}
            onSingleClick={onSingleClick}
            t={t}
          />
        </div>

        <Footer t={t} feature={feature} />
      </div>
    </div>
  );
};

export async function aiCreditModalShow(options: PaymentModalProps) {
  const { feature, reason } = options;
  trackEvent(EventNewID.SUBSCRIPTION_POP_EXP, {
    source: feature,
    reason,
    subscribe_type: SubscriptionTypeEnum.credit,
  });
  CommonModal.show((t) => ({
    width: 1280,
    footer: false,
    customPadding: {
      x: 0,
      y: 0,
    },
    modalClassName: 'ai-credit-modal',
    content: (close) => (
      <PaymentContent
        {...options}
        t={t}
        onClose={() => {
          close();
          options.onClose?.();
        }}
      />
    ),
  }));
}
